import * as React from "react";

import { getInputMethodFromEvent, InputMethod, isIE } from "@1js/acui-common";

/**
 * Hook to track which pointer method has been last used with the button and returns a function to retrieve
 * the method given a particular DOM event
 */
export const useFunctionGetInputMethod = (state: Record<string, any>) => {
  const pointerDownEventIdRef = React.useRef<number | undefined>(undefined);
  const pointerInputMethodRef = React.useRef<InputMethod | undefined>(
    undefined
  );

  state.root.onPointerDown = React.useCallback(
    (ev: React.PointerEvent<HTMLElement>) => {
      pointerDownEventIdRef.current = ev.pointerId;
    },
    []
  );

  state.root.onPointerUp = React.useCallback(
    (ev: React.PointerEvent<HTMLElement>) => {
      if (pointerDownEventIdRef.current === ev.pointerId) {
        switch (ev.pointerType) {
          case "touch":
            pointerInputMethodRef.current = InputMethod.Touch;
            break;
          case "pen":
            pointerInputMethodRef.current = InputMethod.Pen;
            break;
          case "mouse":
            pointerInputMethodRef.current = InputMethod.Mouse;
            break;
          default:
            pointerInputMethodRef.current = InputMethod.Unknown;
        }
      }
      pointerDownEventIdRef.current = undefined;
    },
    []
  );

  const getInputMethod = React.useCallback(
    (
      ev?: React.MouseEvent<HTMLElement, MouseEvent>
    ): InputMethod | undefined => {
      const inputMethod = pointerInputMethodRef.current;
      // if inputMethod is not Touch or Pen and we have a mouse event, lets attempt to get
      // the input method from event instead of the pointer.  In cases where the event is undefined
      // we will always use pointer.  For example, flyout anchors don't pass an event.
      // However, IE will have ev.native always as 0 which will make us set
      // inputMethod for clicks to Keyboard, we know this is a mouse click so lets just return
      // the correct inputMethod
      if (
        ev &&
        inputMethod !== InputMethod.Pen &&
        inputMethod !== InputMethod.Touch &&
        !(isIE() && inputMethod === InputMethod.Mouse)
      ) {
        return getInputMethodFromEvent(ev);
      }
      return inputMethod;
    },
    []
  );

  return getInputMethod;
};
